import { Alert, Avatar, Button, Card, Col, Descriptions, Divider, Drawer, Empty, Form, Input, List, QRCode, Row, Select, Slider, Space, Statistic, Tabs, Tooltip, Typography, Upload, message } from 'antd';
import { CameraOutlined, CheckCircleOutlined, ClockCircleOutlined, DeleteOutlined, SafetyCertificateOutlined, UserOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import IncidentListTable from '../components/IncidentListTable';
import { startTotpSetup, verifyTotpSetupCode } from '../services/auth';
import {
  createUserAssignment,
  deleteUserAssignment,
  getCurrentUser,
  getMyContributions,
  listFactories,
  listOrganizations,
  listServiceLocations,
  updateMyProfile,
  ApiRequestError,
} from '../services/api';
import type {
  CurrentUser,
  Factory,
  Incident,
  IncidentAttachment,
  Organization,
  ServiceLocation,
  IncidentTask,
  UserAssignmentCreate,
  UserContributionDay,
  UserContributionSummary,
  UserProfileUpdate,
  UserRole,
} from '../types/incident';

const { Paragraph, Text } = Typography;

interface Props {
  currentUser: CurrentUser | null;
  incidents: Incident[];
  attachmentsByIncident: Record<string, IncidentAttachment[]>;
  tasksByIncident: Record<string, IncidentTask[]>;
  onUserUpdated: (user: CurrentUser) => void;
}

const isClosedTask = (task: IncidentTask): boolean => ['RESOLVED', 'CLOSED'].includes(task.status);
const isOpenTask = (task: IncidentTask): boolean => !isClosedTask(task);

const roleOptions: UserRole[] = ['SERVICE', 'QUALITY', 'ISSUING_PARTY', 'ORG_ADMIN', 'SYSTEM_ADMIN'];
const contributionLevels = [0, 1, 3, 6];
const profileImageMaxBytes = 5 * 1024 * 1024;

const getContributionLevel = (count: number) => {
  if (count <= contributionLevels[0]) return 0;
  if (count <= contributionLevels[1]) return 1;
  if (count <= contributionLevels[2]) return 2;
  if (count <= contributionLevels[3]) return 3;
  return 4;
};

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(String(reader.result)));
    reader.addEventListener('error', () => reject(reader.error));
    reader.readAsDataURL(file);
  });

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', reject);
    image.src = src;
  });

const cropProfileImage = async (imageSrc: string, cropPixels: Area): Promise<string> => {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  const size = 256;
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Canvas is not available');
  }

  canvas.width = size;
  canvas.height = size;
  context.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    size,
    size
  );
  return canvas.toDataURL('image/jpeg', 0.9);
};

const MyPage = ({ currentUser, incidents, attachmentsByIncident, tasksByIncident, onUserUpdated }: Props) => {
  const { t, i18n } = useTranslation();
  const [messageApi, messageContextHolder] = message.useMessage();
  const [form] = Form.useForm<UserAssignmentCreate>();
  const [profileForm] = Form.useForm<UserProfileUpdate>();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [factories, setFactories] = useState<Factory[]>([]);
  const [serviceLocations, setServiceLocations] = useState<ServiceLocation[]>([]);
  const [selectedRole, setSelectedRole] = useState<UserRole>('SERVICE');
  const [saving, setSaving] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const [contributions, setContributions] = useState<UserContributionSummary | null>(null);
  const [profileImageDraft, setProfileImageDraft] = useState<string | null>(null);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [cropApplying, setCropApplying] = useState(false);
  const [mfaSetupUri, setMfaSetupUri] = useState<string | null>(null);
  const [mfaSharedSecret, setMfaSharedSecret] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaPreparing, setMfaPreparing] = useState(false);
  const [mfaVerifying, setMfaVerifying] = useState(false);

  useEffect(() => {
    Promise.all([
      listOrganizations().catch(() => []),
      listFactories().catch(() => []),
      listServiceLocations().catch(() => []),
    ]).then(([organizationItems, factoryItems, serviceLocationItems]) => {
      setOrganizations(organizationItems);
      setFactories(factoryItems);
      setServiceLocations(serviceLocationItems);
    });
  }, []);

  useEffect(() => {
    getMyContributions().then(setContributions).catch(() => setContributions(null));
  }, []);

  useEffect(() => {
    if (!profileDrawerOpen) return;
    profileForm.setFieldsValue({
      display_name: currentUser?.display_name || '',
      email: currentUser?.email || '',
      job_title: currentUser?.job_title || '',
      profile_image_url: currentUser?.profile_image_url || '',
    });
    setProfileImageDraft(currentUser?.profile_image_url || null);
    setMfaSetupUri(null);
    setMfaSharedSecret(null);
    setMfaCode('');
  }, [
    currentUser?.display_name,
    currentUser?.email,
    currentUser?.job_title,
    currentUser?.profile_image_url,
    profileDrawerOpen,
    profileForm,
  ]);

  const roles = useMemo(
    () => currentUser?.assignments.map((assignment) => assignment.role) || [],
    [currentUser?.assignments]
  );

  const { relatedIncidents, openIncidents, completedIncidents } = useMemo(() => {
    const related = incidents.filter((incident) => {
      const tasks = tasksByIncident[incident.id] || [];
      const reportedByMe = currentUser?.id && incident.reporter_user_id === currentUser.id;
      const roleTask = tasks.some((task) => roles.includes(task.responsible_role));
      const issuingPartyReview = roles.includes('ISSUING_PARTY') && incident.status === 'RESOLVED';
      return reportedByMe || roleTask || issuingPartyReview;
    });

    const isOpenForCurrentUser = (incident: Incident) => {
      const roleTasks = (tasksByIncident[incident.id] || []).filter((task) => roles.includes(task.responsible_role));
      const hasOpenRoleTask = roleTasks.some(isOpenTask);
      const needsIssuingPartyAction = roles.includes('ISSUING_PARTY') && incident.status !== 'CLOSED';
      return hasOpenRoleTask || needsIssuingPartyAction;
    };

    const open = related.filter(isOpenForCurrentUser);
    const completed = related.filter((incident) => !isOpenForCurrentUser(incident));

    return {
      relatedIncidents: related,
      openIncidents: open,
      completedIncidents: completed,
    };
  }, [currentUser?.id, incidents, roles, tasksByIncident]);

  const contributionCells = useMemo<Array<UserContributionDay | null>>(() => {
    if (!contributions?.items.length) return [];
    const firstDate = new Date(`${contributions.items[0].date}T00:00:00`);
    return [...Array.from({ length: firstDate.getDay() }, () => null), ...contributions.items];
  }, [contributions]);
  const contributionColumnCount = Math.ceil(contributionCells.length / 7);
  const contributionMonthLabels = useMemo(() => {
    const labels: Array<{ key: string; label: string; column: number }> = [];
    let previousMonth = '';
    contributionCells.forEach((day, index) => {
      if (!day) return;
      const monthKey = day.date.slice(0, 7);
      if (monthKey === previousMonth) return;
      const date = new Date(`${day.date}T00:00:00`);
      labels.push({
        key: monthKey,
        label: date.toLocaleString(i18n.language.startsWith('ja') ? 'ja-JP' : 'en-US', { month: 'short' }),
        column: Math.floor(index / 7) + 1,
      });
      previousMonth = monthKey;
    });
    return labels;
  }, [contributionCells, i18n.language]);
  const contributionGridTemplate = `repeat(${contributionColumnCount}, minmax(10px, 1fr))`;

  const refreshUser = async () => {
    const user = await getCurrentUser();
    onUserUpdated(user);
  };

  const openProfileDrawer = () => {
    profileForm.setFieldsValue({
      display_name: currentUser?.display_name || '',
      email: currentUser?.email || '',
      job_title: currentUser?.job_title || '',
      profile_image_url: currentUser?.profile_image_url || '',
    });
    setProfileImageDraft(currentUser?.profile_image_url || null);
    setCropImageSrc(null);
    setMfaSetupUri(null);
    setMfaSharedSecret(null);
    setMfaCode('');
    setProfileDrawerOpen(true);
  };

  const handleAddAssignment = async (values: UserAssignmentCreate) => {
    setSaving(true);
    try {
      await createUserAssignment(values);
      await refreshUser();
      form.resetFields();
      form.setFieldValue('role', selectedRole);
      messageApi.success(t('myPage.assignments.saved'));
    } catch {
      messageApi.error(t('myPage.assignments.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId: string) => {
    setSaving(true);
    try {
      await deleteUserAssignment(assignmentId);
      await refreshUser();
      messageApi.success(t('myPage.assignments.deleted'));
    } catch {
      messageApi.error(t('myPage.assignments.deleteFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfile = async (values: UserProfileUpdate) => {
    setProfileSaving(true);
    try {
      const user = await updateMyProfile({
        display_name: values.display_name?.trim() || null,
        email: values.email?.trim() || null,
        job_title: values.job_title || null,
        profile_image_url: values.profile_image_url || null,
      });
      onUserUpdated(user);
      setProfileDrawerOpen(false);
      messageApi.success(t('myPage.profile.saved'));
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 409) {
        messageApi.error(t('myPage.profile.emailAlreadyUsed'));
      } else {
        messageApi.error(t('myPage.profile.saveFailed'));
      }
    } finally {
      setProfileSaving(false);
    }
  };

  const handleSelectProfileImage = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      messageApi.error(t('myPage.profile.imageFileInvalid'));
      return;
    }
    if (file.size > profileImageMaxBytes) {
      messageApi.error(t('myPage.profile.imageFileTooLarge'));
      return;
    }

    try {
      const imageUrl = await readFileAsDataUrl(file);
      setCropImageSrc(imageUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    } catch {
      messageApi.error(t('myPage.profile.imageReadFailed'));
    }
  };

  const handleApplyProfileImageCrop = async () => {
    if (!cropImageSrc || !croppedAreaPixels) return;
    setCropApplying(true);
    try {
      const croppedImage = await cropProfileImage(cropImageSrc, croppedAreaPixels);
      profileForm.setFieldValue('profile_image_url', croppedImage);
      setProfileImageDraft(croppedImage);
      setCropImageSrc(null);
    } catch {
      messageApi.error(t('myPage.profile.imageCropFailed'));
    } finally {
      setCropApplying(false);
    }
  };

  const handleStartMfaSetup = async () => {
    setMfaPreparing(true);
    try {
      const setup = await startTotpSetup();
      setMfaSetupUri(setup.setupUri);
      setMfaSharedSecret(setup.sharedSecret);
      setMfaCode('');
      messageApi.success(t('myPage.profile.mfaSetupStarted'));
    } catch {
      messageApi.error(t('myPage.profile.mfaSetupFailed'));
    } finally {
      setMfaPreparing(false);
    }
  };

  const handleVerifyMfaSetup = async () => {
    if (!mfaCode.trim()) {
      messageApi.error(t('myPage.profile.mfaCodeRequired'));
      return;
    }
    setMfaVerifying(true);
    try {
      await verifyTotpSetupCode(mfaCode.trim());
      setMfaSetupUri(null);
      setMfaSharedSecret(null);
      setMfaCode('');
      messageApi.success(t('myPage.profile.mfaSetupSaved'));
    } catch {
      messageApi.error(t('myPage.profile.mfaVerifyFailed'));
    } finally {
      setMfaVerifying(false);
    }
  };

  const renderContributionTooltip = (day: UserContributionDay) => (
    <div className="contributionTooltip">
      <Text strong>{new Date(`${day.date}T00:00:00`).toLocaleDateString()}</Text>
      <Text>{t('myPage.contributions.tooltip.total', { count: day.count })}</Text>
      <Text type="secondary">{t('myPage.contributions.breakdown.createIncident', { count: day.create_incident })}</Text>
      <Text type="secondary">{t('myPage.contributions.breakdown.updateIncident', { count: day.update_incident })}</Text>
      <Text type="secondary">{t('myPage.contributions.breakdown.taskActivity', { count: day.task_activity })}</Text>
      <Text type="secondary">{t('myPage.contributions.breakdown.closeRequestTask', { count: day.close_request_task })}</Text>
      <Text type="secondary">{t('myPage.contributions.breakdown.addAttachment', { count: day.add_attachment })}</Text>
    </div>
  );

  const contributionCalendar = contributions ? (
    <Card>
      <Row gutter={[16, 16]} className="contributionStats">
        <Col xs={12} md={6}>
          <Statistic title={t('myPage.contributions.stats.total')} value={contributions.total} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title={t('myPage.contributions.stats.activeDays')} value={contributions.active_days} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title={t('myPage.contributions.stats.currentStreak')} value={contributions.current_streak} suffix={t('myPage.contributions.stats.days')} />
        </Col>
        <Col xs={12} md={6}>
          <Statistic title={t('myPage.contributions.stats.longestStreak')} value={contributions.longest_streak} suffix={t('myPage.contributions.stats.days')} />
        </Col>
      </Row>
      <div className="contributionCalendarWrap" aria-label={t('myPage.contributions.title')}>
        <div className="contributionWeekdays" aria-hidden="true">
          <span />
          <span>{t('myPage.contributions.weekdays.mon')}</span>
          <span />
          <span>{t('myPage.contributions.weekdays.wed')}</span>
          <span />
          <span>{t('myPage.contributions.weekdays.fri')}</span>
          <span />
        </div>
        <div className="contributionChartArea">
          <div
            className="contributionMonths"
            style={{ gridTemplateColumns: contributionGridTemplate }}
            aria-hidden="true"
          >
            {contributionMonthLabels.map((month) => (
              <span key={month.key} style={{ gridColumnStart: month.column }}>
                {month.label}
              </span>
            ))}
          </div>
          <div
            className="contributionCalendar"
            style={{ gridTemplateColumns: contributionGridTemplate }}
          >
            {contributionCells.map((day, index) =>
              day ? (
                <Tooltip key={day.date} title={renderContributionTooltip(day)}>
                  <span
                    className={`contributionCell contributionLevel${getContributionLevel(day.count)}`}
                    aria-label={t('myPage.contributions.ariaDay', { date: day.date, count: day.count })}
                  />
                </Tooltip>
              ) : (
                <span key={`empty-${index}`} className="contributionCell contributionCellEmpty" />
              )
            )}
          </div>
        </div>
      </div>
      <div className="contributionFooter">
        <Paragraph className="contributionNote" type="secondary">
          {t('myPage.contributions.description')}
        </Paragraph>
        <div className="contributionLegend">
          <Text type="secondary">{t('myPage.contributions.less')}</Text>
          {[0, 1, 2, 3, 4].map((level) => (
            <span key={level} className={`contributionCell contributionLevel${level}`} />
          ))}
          <Text type="secondary">{t('myPage.contributions.more')}</Text>
        </div>
      </div>
    </Card>
  ) : null;

  const taskTab = (
    <div className="detailTabStack">
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title={t('myPage.stats.related')} value={relatedIncidents.length} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title={t('myPage.stats.open')} value={openIncidents.length} prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title={t('myPage.stats.completed')} value={completedIncidents.length} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
      </Row>

      {contributionCalendar}

      <Card title={t('myPage.sections.related')}>
        <IncidentListTable
          incidents={openIncidents}
          attachmentsByIncident={attachmentsByIncident}
          tasksByIncident={tasksByIncident}
          currentUser={currentUser}
          filterControls={['keyword', 'status', 'severity']}
        />
      </Card>

      <Card title={t('myPage.sections.completed')}>
        <IncidentListTable
          incidents={completedIncidents}
          attachmentsByIncident={attachmentsByIncident}
          tasksByIncident={tasksByIncident}
          currentUser={currentUser}
          filterControls={['keyword', 'status', 'severity']}
        />
      </Card>
    </div>
  );

  const profileTab = (
    <div className="detailTabStack">
      <Card
        title={t('myPage.profile.cardTitle')}
        extra={
          <Button type="primary" onClick={openProfileDrawer}>
            {t('myPage.profile.edit')}
          </Button>
        }
      >
        <div className="profileHeader">
          <div className="profileAvatarDisplay">
            <Avatar size={88} src={currentUser?.profile_image_url || undefined} icon={<UserOutlined />} />
          </div>
          <div className="profileContent">
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t('myPage.profile.name')}>
                {currentUser?.display_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('myPage.profile.email')}>
                <Space size={8} wrap>
                  <span>{currentUser?.email || '-'}</span>
                  {currentUser && !currentUser.email_is_validated && (
                    <Text type="warning">{t('myPage.profile.emailUnvalidated')}</Text>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('myPage.profile.jobTitle')}>
                {currentUser?.job_title || '-'}
              </Descriptions.Item>
            </Descriptions>
            {currentUser && !currentUser.email_is_validated && (
              <Alert
                className="profileValidationAlert"
                type="warning"
                showIcon
                message={t('myPage.profile.emailValidationRequired')}
                description={<Link to="/email-verification">{t('myPage.profile.emailValidationLink')}</Link>}
              />
            )}
          </div>
        </div>
      </Card>

      <Card title={t('myPage.assignments.title')}>
        <List
          locale={{ emptyText: <Empty description={t('myPage.assignments.empty')} /> }}
          dataSource={currentUser?.assignments || []}
          renderItem={(assignment) => (
            <List.Item
              actions={[
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  loading={saving}
                  onClick={() => handleDeleteAssignment(assignment.id)}
                >
                  {t('myPage.assignments.delete')}
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={assignment.label}
                description={t(`incident.role.${assignment.role}`)}
              />
            </List.Item>
          )}
        />

        <Form
          className="assignmentForm"
          form={form}
          layout="vertical"
          initialValues={{ role: selectedRole }}
          onFinish={handleAddAssignment}
        >
          <Form.Item label={t('myPage.assignments.role')} name="role" rules={[{ required: true }]}>
            <Select
              onChange={(role) => {
                setSelectedRole(role);
                form.setFieldsValue({ organization_id: null, factory_id: null, service_location_id: null });
              }}
              options={roleOptions.map((role) => ({ value: role, label: t(`incident.role.${role}`) }))}
            />
          </Form.Item>
          {selectedRole === 'SERVICE' && (
            <Form.Item label={t('myPage.assignments.serviceLocation')} name="service_location_id" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={serviceLocations.map((location) => ({
                  value: location.id,
                  label: `${location.name} (${location.region_code})`,
                }))}
              />
            </Form.Item>
          )}
          {selectedRole === 'QUALITY' && (
            <Form.Item label={t('myPage.assignments.factory')} name="factory_id" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={factories.map((factory) => ({
                  value: factory.id,
                  label: `${factory.name} (${factory.region_code || '-'})`,
                }))}
              />
            </Form.Item>
          )}
          {['ISSUING_PARTY', 'ORG_ADMIN', 'SYSTEM_ADMIN'].includes(selectedRole) && (
            <Form.Item label={t('myPage.assignments.organization')} name="organization_id" rules={[{ required: true }]}>
              <Select
                showSearch
                optionFilterProp="label"
                options={organizations.map((organization) => ({
                  value: organization.id,
                  label: organization.name,
                }))}
              />
            </Form.Item>
          )}
          <Button type="primary" htmlType="submit" loading={saving}>
            {t('myPage.assignments.add')}
          </Button>
        </Form>
      </Card>
    </div>
  );

  return (
    <div className="page">
      {messageContextHolder}
      <Drawer
        title={t('myPage.profile.editTitle')}
        open={profileDrawerOpen}
        width={520}
        onClose={() => setProfileDrawerOpen(false)}
        destroyOnClose
        footer={
          <Space className="drawerFooterActions">
            <Button onClick={() => setProfileDrawerOpen(false)}>{t('common.cancel')}</Button>
            <Button type="primary" loading={profileSaving} onClick={() => profileForm.submit()}>
              {t('myPage.profile.save')}
            </Button>
          </Space>
        }
      >
        <Form
          className="profileForm"
          form={profileForm}
          layout="vertical"
          onFinish={handleSaveProfile}
        >
          <div className="profileDrawerPhoto">
            <Avatar size={96} src={profileImageDraft || undefined} icon={<UserOutlined />} />
            <Upload
              accept="image/*"
              maxCount={1}
              showUploadList={false}
              beforeUpload={(file) => {
                handleSelectProfileImage(file);
                return Upload.LIST_IGNORE;
              }}
            >
              <Button icon={<CameraOutlined />}>{t('myPage.profile.changeImage')}</Button>
            </Upload>
          </div>
          {cropImageSrc && (
            <div className="profileCropModalBody">
              <div className="profileCropperFrame">
                <Cropper
                  image={cropImageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_, croppedPixels) => setCroppedAreaPixels(croppedPixels)}
                />
              </div>
              <div className="profileCropControls">
                <Text type="secondary">{t('myPage.profile.zoom')}</Text>
                <Slider min={1} max={3} step={0.1} value={zoom} onChange={setZoom} />
                <Space>
                  <Button type="primary" loading={cropApplying} onClick={handleApplyProfileImageCrop}>
                    {t('myPage.profile.applyCrop')}
                  </Button>
                  <Button onClick={() => setCropImageSrc(null)}>{t('myPage.profile.cancelCrop')}</Button>
                </Space>
              </div>
            </div>
          )}
          <Form.Item
            label={t('myPage.profile.name')}
            name="display_name"
            rules={[{ required: true, message: t('validation.required') }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t('myPage.profile.email')}
            name="email"
            rules={[
              { required: true, message: t('validation.required') },
              { type: 'email', message: t('myPage.profile.emailInvalid') },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item label={t('myPage.profile.jobTitle')} name="job_title">
            <Input placeholder={t('myPage.profile.jobTitlePlaceholder')} allowClear />
          </Form.Item>
          <Form.Item name="profile_image_url" noStyle>
            <Input type="hidden" />
          </Form.Item>
        </Form>
        <Divider />
        <div className="profileMfaSection">
          <div className="profileMfaHeader">
            <SafetyCertificateOutlined />
            <Text strong>{t('myPage.profile.mfaTitle')}</Text>
          </div>
          <Paragraph type="secondary" className="profileMfaDescription">
            {t('myPage.profile.mfaDescription')}
          </Paragraph>
          <Space direction="vertical" size={12} className="profileMfaControls">
            <Button loading={mfaPreparing} onClick={handleStartMfaSetup}>
              {t('myPage.profile.mfaStart')}
            </Button>
            {mfaSharedSecret && (
              <>
                <Alert
                  type="info"
                  showIcon
                  message={t('myPage.profile.mfaSecretLabel')}
                  description={
                    <Space direction="vertical" size={8} className="profileMfaControls">
                      <Input value={mfaSharedSecret} readOnly />
                      {mfaSetupUri && (
                        <div className="profileMfaQrCode">
                          <QRCode value={mfaSetupUri} size={176} />
                        </div>
                      )}
                      {mfaSetupUri && (
                        <Button href={mfaSetupUri}>
                          {t('myPage.profile.mfaOpenAuthenticator')}
                        </Button>
                      )}
                    </Space>
                  }
                />
                <Input
                  inputMode="numeric"
                  maxLength={6}
                  placeholder={t('myPage.profile.mfaCodePlaceholder')}
                  value={mfaCode}
                  onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                />
                <Button type="primary" loading={mfaVerifying} onClick={handleVerifyMfaSetup}>
                  {t('myPage.profile.mfaVerify')}
                </Button>
              </>
            )}
          </Space>
        </div>
      </Drawer>
      <Tabs
        className="incidentDetailTabs"
        items={[
          { key: 'tasks', label: t('myPage.tabs.tasks'), children: taskTab },
          { key: 'profile', label: t('myPage.tabs.profile'), children: profileTab },
        ]}
      />
    </div>
  );
};

export default MyPage;
